import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { Loader } from "@package/subframe/components/Loader";
import { Select } from "@package/subframe/components/Select";
import { FeatherDownload } from "@subframe/core";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { isEmergencyLightingPass } from "@/components/logs/emergency-lighting";
import { Pagination } from "@/components/ui/pagination";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import {
  useListLogHistoryQueryControllerList,
  useListLogTypesQueryControllerList,
} from "@/generated/log-types/log-types";
import type {
  LogEntryFollowUpActionDto,
  LogHistorySessionDto,
} from "@/generated/model";
import { useListRoomsQueryControllerList } from "@/generated/rooms/rooms";
import {
  FIRE_DOOR_INSPECTION_LOG_TYPE_SLUG,
  fireDoorLabel,
  isFireDoorInspectionPass,
} from "@/lib/fire-alarm-tests";
import { memberDisplayName } from "@/lib/member-display";
import {
  EMERGENCY_LIGHTING_LOG_TYPE_SLUG,
  FRIDGE_TEMPERATURE_LOG_TYPE_SLUG,
  GARDEN_SUPERVISION_LOG_TYPE_SLUG,
  SLEEP_CHECK_LOG_TYPE_SLUG,
} from "@/lib/monitoring";
import {
  downloadMonitoringCsv,
  exportErrorMessage,
} from "@/lib/monitoring-export";
import { DEFAULT_PAGE_SIZE } from "@/lib/pagination";
import { canRaiseFollowUp } from "@/lib/roles";

interface LogHistoryTabProps {
  startDate: Date;
  endDate: Date;
}

export function LogHistoryTab({ startDate, endDate }: LogHistoryTabProps) {
  const { selectedNurseryId } = useNursery();
  const nurseryId = selectedNurseryId ?? "";
  const [logTypeFilter, setLogTypeFilter] = useState<string>("");
  const [roomFilter, setRoomFilter] = useState<string>("");
  const [page, setPage] = useState(1); // 1-based, matches the Pagination component
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isExporting, setIsExporting] = useState(false);

  // The date range lives in the dashboard shell, so it is passed explicitly;
  // otherwise the export would use the server's default window.
  const handleExport = async () => {
    if (!nurseryId || isExporting) return;
    setIsExporting(true);
    try {
      await downloadMonitoringCsv(nurseryId, {
        scope: "history",
        logTypeId: logTypeFilter || undefined,
        locationId: roomFilter || undefined,
        from: startDate.toISOString(),
        to: endDate.toISOString(),
      });
    } catch (error) {
      toast.error(
        (await exportErrorMessage(error)) ??
          "Failed to export — please try again",
      );
    } finally {
      setIsExporting(false);
    }
  };

  // The date range lives on the dashboard shell, so a period change arrives as
  // a prop rather than through a handler that could reset the page itself.
  const rangeKey = `${startDate.toISOString()}-${endDate.toISOString()}`;
  useEffect(() => {
    setPage(1);
  }, [rangeKey]);

  const { data: logTypesResponse } =
    useListLogTypesQueryControllerList(nurseryId);
  const logTypes = logTypesResponse?.data;

  const { data: roomsResponse } = useListRoomsQueryControllerList(
    nurseryId,
    undefined,
    { query: { enabled: !!nurseryId } },
  );
  const rooms = roomsResponse?.data;

  const { data: sessionsResponse, isLoading } =
    useListLogHistoryQueryControllerList(
      nurseryId,
      {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        logTypeId: logTypeFilter || undefined,
        roomId: roomFilter || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      },
      { query: { refetchOnMount: "always" } },
    );

  const filteredSessions = useMemo(
    () => sessionsResponse?.data.data ?? [],
    [sessionsResponse],
  );
  const total = sessionsResponse?.data.total ?? filteredSessions.length;

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Filters */}
      <div className="flex w-full flex-wrap items-end gap-3">
        <Select
          label="Log Type"
          placeholder="All log types"
          value={logTypeFilter || "all"}
          onValueChange={(v) => {
            setLogTypeFilter(v === "all" ? "" : v);
            setPage(1);
          }}
        >
          <Select.Item value="all">All log types</Select.Item>
          {(logTypes ?? [])
            .filter(
              (lt) =>
                lt.slug === SLEEP_CHECK_LOG_TYPE_SLUG ||
                lt.slug === GARDEN_SUPERVISION_LOG_TYPE_SLUG ||
                lt.slug === FRIDGE_TEMPERATURE_LOG_TYPE_SLUG ||
                lt.slug === EMERGENCY_LIGHTING_LOG_TYPE_SLUG,
            )
            .map((lt) => (
              <Select.Item key={lt.id} value={lt.id}>
                {lt.name}
              </Select.Item>
            ))}
        </Select>

        <Select
          label="Room"
          placeholder="All rooms"
          value={roomFilter || "all"}
          onValueChange={(v) => {
            setRoomFilter(v === "all" ? "" : v);
            setPage(1);
          }}
        >
          <Select.Item value="all">All rooms</Select.Item>
          {(rooms ?? []).map((g) => (
            <Select.Item key={g.id} value={g.id}>
              {g.name}
            </Select.Item>
          ))}
        </Select>

        <Button
          className="ml-auto"
          variant="neutral-secondary"
          icon={<FeatherDownload />}
          onClick={handleExport}
          disabled={isExporting}
        >
          {isExporting ? "Exporting..." : "Export CSV"}
        </Button>
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="flex w-full items-center justify-center py-12">
          <Loader size="large" />
        </div>
      ) : filteredSessions.length === 0 ? (
        <div className="flex w-full flex-col items-center justify-center gap-2 py-12">
          <span className="text-body font-body text-subtext-color">
            No log entries found for this period
          </span>
        </div>
      ) : (
        <div className="flex w-full flex-col gap-4">
          {filteredSessions.map((session) => (
            <SessionCard key={session.id} session={session} />
          ))}

          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={total}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            itemNoun="log sessions"
          />
        </div>
      )}
    </div>
  );
}

const FOLLOW_UP_STATUS_BADGES: Record<
  LogEntryFollowUpActionDto["status"],
  { label: string; variant: "neutral" | "error" | "warning" | "success" }
> = {
  OPEN: { label: "Open", variant: "neutral" },
  OVERDUE: { label: "Overdue", variant: "error" },
  WAITING_APPROVAL: { label: "Waiting approval", variant: "warning" },
  COMPLETED: { label: "Completed", variant: "success" },
  CANCELLED: { label: "Cancelled", variant: "neutral" },
};

function SessionCard({ session }: { session: LogHistorySessionDto }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const { selectedNurseryId, nurseryRole } = useNursery();
  const { isPlatformAdmin, userRoleType } = useAuth();
  const isEmergencyLighting =
    session.logType.slug === EMERGENCY_LIGHTING_LOG_TYPE_SLUG;
  const isFireDoorInspection =
    session.logType.slug === FIRE_DOOR_INSPECTION_LOG_TYPE_SLUG;
  // Rendered as a Pass/Fail badge, with a follow-up offered on a fail.
  const isPassFailCheck = isEmergencyLighting || isFireDoorInspection;
  // Gated like the check detail page: creating the follow-up 403s for anyone else.
  const canRaise = canRaiseFollowUp({
    isPlatformAdmin,
    userRoleType,
    nurseryRole,
  });

  return (
    <div className="flex w-full flex-col rounded-lg border border-neutral-200">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between px-4 py-3 hover:bg-neutral-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span className="text-body-bold font-body-bold text-default-font">
            {session.logType.name}
          </span>
          <span className="text-caption font-caption text-subtext-color">
            {format(new Date(session.date), "d MMM yyyy")}
          </span>
        </div>
        <Badge variant="neutral">
          {session.entryCount} {session.entryCount === 1 ? "entry" : "entries"}
        </Badge>
      </button>

      {expanded && session.entries.length > 0 && (
        <div className="flex w-full flex-col gap-2 border-t border-neutral-200 px-4 py-3">
          {session.entries.map((entry) => {
            const name = memberDisplayName(entry.recordedBy);
            const data = entry.data as Record<string, unknown>;
            const ack = entry.acknowledgement;
            const ackName = ack?.acknowledgedBy
              ? memberDisplayName(ack.acknowledgedBy)
              : "a manager";
            const passed = isFireDoorInspection
              ? isFireDoorInspectionPass(data)
              : isEmergencyLightingPass(data);

            return (
              <div
                key={entry.id}
                className="flex w-full flex-col gap-1 rounded border border-neutral-100 px-3 py-2"
              >
                {entry.mealName && (
                  <span className="text-caption-bold font-caption-bold text-default-font">
                    {entry.mealName}
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-caption font-caption text-subtext-color">
                    {format(new Date(entry.timestamp), "HH:mm")} &middot; {name}
                  </span>
                  <div className="flex items-center gap-2">
                    {isPassFailCheck && (
                      <Badge variant={passed ? "success" : "error"}>
                        {passed ? "Pass" : "Fail"}
                      </Badge>
                    )}
                    {entry.room && (
                      <Badge variant="neutral">{entry.room.name}</Badge>
                    )}
                    {entry.fireAlarmCallPoint && (
                      <Badge variant="neutral">
                        Call point: {entry.fireAlarmCallPoint.name}
                      </Badge>
                    )}
                    {entry.fireDoor && (
                      <Badge variant="neutral">
                        Door: {fireDoorLabel(entry.fireDoor)}
                      </Badge>
                    )}
                  </div>
                </div>
                {!isPassFailCheck && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {Object.entries(data).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-1">
                        <span className="text-caption font-caption text-subtext-color capitalize">
                          {key.replace(/([A-Z])/g, " $1").trim()}:
                        </span>
                        <span className="text-caption font-caption-bold text-default-font">
                          {typeof value === "boolean"
                            ? value
                              ? "Yes"
                              : "No"
                            : String(value ?? "-")}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {entry.notes && (
                  <span className="text-caption font-caption text-subtext-color italic">
                    {entry.notes}
                  </span>
                )}
                {ack && (
                  <span className="text-caption font-caption text-success-700">
                    Acknowledged by {ackName} &middot;{" "}
                    {format(new Date(ack.acknowledgedAt), "PPp")}
                  </span>
                )}
                {(entry.followUpActions ?? []).map((action) => {
                  const badge = FOLLOW_UP_STATUS_BADGES[action.status];
                  return (
                    <div
                      key={action.id}
                      className="flex w-full items-center justify-between gap-2 rounded bg-neutral-50 px-2 py-1.5"
                    >
                      <span className="text-caption font-caption text-default-font line-clamp-1">
                        Follow-up: {action.description}
                      </span>
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                    </div>
                  );
                })}
                {isPassFailCheck && !passed && canRaise && (
                  <div className="flex w-full justify-end pt-1">
                    <Button
                      variant="neutral-secondary"
                      size="small"
                      onClick={() =>
                        navigate({
                          to: "/follow-up-actions/create",
                          search: {
                            nurseryId: selectedNurseryId ?? "",
                            scheduledQuestionnaireId: "",
                            answerId: "",
                            questionText: "",
                            checklistName: "",
                            logEntryId: entry.id,
                            logEntryContext: [
                              session.logType.name,
                              entry.room?.name,
                              entry.fireAlarmCallPoint?.name,
                              entry.fireDoor && fireDoorLabel(entry.fireDoor),
                            ]
                              .filter(Boolean)
                              .join(" — "),
                          },
                        })
                      }
                    >
                      Raise follow-up
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
